import { Translate } from "components/common/Translate/Translate";
import { Stars } from "./Stars/Stars";
import { BackgroundColorInput } from "./BackgroundColorInput/BackgroundColorInput";
import { useAppSelector } from "store/store";
import { useState } from "react";
import { TextColorInput } from "./TextColorInput/TextColorInput";
import { calculateContrast } from "store/features/colorPicker/utils/calculateContrast";
import tinycolor from "tinycolor2";
import { useRouter } from "next/router";
import { translateFunc } from "client/utils/translateFunc";
import { capitalize } from "client/utils/capitalize";
import { ToolTip } from "components/common/ToolTip/ToolTip";

const WHITE = "#FFFFFF";
const BLACK = "#0000000";

export const ContrastChecker = () => {
    const selectedColor = useAppSelector((store) => store.colorPicker.selectedColor);
    const { asPath } = useRouter();
    const colorQuery = asPath.split("?")[1]?.split("=")[1] || "";
    const [textColor, setTextColor] = useState(tinycolor(colorQuery).isDark() ? WHITE : BLACK);
    const contrast = selectedColor ? calculateContrast(textColor, selectedColor.hex) : 0;
    const scores = getScores(contrast);
    const isAdjustButtonDisabled = selectedColor
        ? tinycolor(selectedColor.hex).isDark()
            ? textColor === WHITE
            : textColor === BLACK
        : false;
    const handleAdjustTextColor = () => {
        if (!selectedColor) return;
        const tiny = tinycolor(selectedColor.hex);
        setTextColor(tiny.isDark() ? WHITE : BLACK);
    };
    return (
        <div className="flex flex-col gap-8 py-48">
            <div className="px-4 text-xl font-semibold lg:px-0">
                <Translate keyName="contrast-checker-title" />
            </div>
            <div>
                <Translate keyName="contrast-checker-preview-text" />
            </div>
            <div className="grid rounded-md lg:grid-cols-2 lg:border border-irGray-400 dark:border-darkSurface-600">
                <div className="flex flex-col gap-4 px-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                        <TextColorInput color={textColor} onChange={setTextColor} />
                        {selectedColor ? <BackgroundColorInput selectedColor={selectedColor} /> : null}
                    </div>
                    <div className="font-semibold capitalize">
                        <Translate keyName="contrast" />
                    </div>
                    <div className="flex flex-col gap-1 overflow-hidden text-black rounded-md">
                        <div
                            className="flex justify-between px-4 py-8"
                            style={{ backgroundColor: getBackgroundColor(scores.cumulative.status) }}
                        >
                            <div className="text-5xl font-semibold">{contrast.toFixed(2)}</div>
                            <div className="flex flex-col items-center gap-1">
                                <div className="capitalize">
                                    <Translate keyName="normal-text" />
                                </div>
                                <Stars score={scores.cumulative.score} />
                                <div className="capitalize">
                                    {`${translateFunc("result")}: ${translateFunc(
                                        getStatusText(scores.cumulative.score),
                                    )}`}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-sm rounded-b-md lg:text-base">
                            <div
                                className="flex flex-col items-center gap-1 px-2 py-8"
                                style={{ backgroundColor: getBackgroundColor(scores.smallText.status) }}
                            >
                                <div className="capitalize">
                                    <Translate keyName="small-text" />
                                </div>
                                <Stars score={scores.smallText.score} />
                                <div className="capitalize">
                                    {`${translateFunc("result")}: ${translateFunc(
                                        getStatusText(scores.smallText.score),
                                    )}`}
                                </div>
                            </div>
                            <div
                                className="flex flex-col items-center justify-between px-2 py-8"
                                style={{ backgroundColor: getBackgroundColor(scores.largeText.status) }}
                            >
                                <div className="capitalize">
                                    <Translate keyName="large-text" />
                                </div>
                                <Stars score={scores.largeText.score} />
                                <div className="capitalize">
                                    {`${translateFunc("result")}: ${translateFunc(
                                        getStatusText(scores.largeText.score),
                                    )}`}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <Translate
                            keyName="contrast-checker-info-text"
                            variables={{
                                smallTextStatus: capitalize(translateFunc(getStatusText(scores.smallText.score))),
                                largeTextStatus: capitalize(translateFunc(getStatusText(scores.largeText.score))),
                            }}
                        />
                    </div>
                    <div>
                        <ToolTip
                            text={translateFunc("adjust-quality-optimal-state-tooltip-text")}
                            inactive={!isAdjustButtonDisabled}
                        >
                            <button
                                className="px-4 py-2 font-semibold text-white capitalize rounded-md bg-irBlue-200 hover:bg-irBlue-400 disabled:bg-gray-500 disabled:cursor-pointer"
                                onClick={handleAdjustTextColor}
                                disabled={isAdjustButtonDisabled}
                            >
                                <Translate keyName="adjust-text-color-button-label" />
                            </button>
                        </ToolTip>
                    </div>
                </div>
                <div
                    className="flex items-center justify-center text-lg font-semibold text-center text-white"
                    style={{ backgroundColor: selectedColor?.hex }}
                >
                    <span className="max-w-[35ch] py-4 leading-9" style={{ color: textColor }}>
                        “Perfection is achieved not when there is nothing more to add, but when there is nothing left to
                        take away.” <br /> — Antoine de Saint-Exupéry
                    </span>
                </div>
            </div>
        </div>
    );
};

enum Status {
    GREEN = "green",
    YELLOW = "yellow",
    RED = "red",
}

interface Score {
    score: number;
    status: Status;
}

interface Scores {
    cumulative: Score;
    smallText: Score;
    largeText: Score;
}

const getScores = (contrast: number): Scores => {
    if (contrast >= 13) {
        return {
            cumulative: {
                score: 5,
                status: Status.GREEN,
            },
            smallText: {
                score: 5,
                status: Status.GREEN,
            },
            largeText: {
                score: 5,
                status: Status.GREEN,
            },
        };
    }
    if (contrast >= 10) {
        return {
            cumulative: {
                score: 4,
                status: Status.GREEN,
            },
            smallText: {
                score: 4,
                status: Status.GREEN,
            },
            largeText: {
                score: 5,
                status: Status.GREEN,
            },
        };
    }
    if (contrast >= 7) {
        return {
            cumulative: {
                score: 3,
                status: Status.YELLOW,
            },
            smallText: {
                score: 3,
                status: Status.YELLOW,
            },
            largeText: {
                score: 4,
                status: Status.GREEN,
            },
        };
    }
    if (contrast >= 3.5) {
        return {
            cumulative: {
                score: 2,
                status: Status.YELLOW,
            },
            smallText: {
                score: 2,
                status: Status.YELLOW,
            },
            largeText: {
                score: 3,
                status: Status.YELLOW,
            },
        };
    }
    return {
        cumulative: {
            score: 1,
            status: Status.RED,
        },
        smallText: {
            score: 1,
            status: Status.RED,
        },
        largeText: {
            score: 1,
            status: Status.RED,
        },
    };
};

const getStatusText = (score: number) => {
    score = Math.max(Math.min(score, 5), 1);
    switch (score) {
        case 5:
            return "perfect";
        case 4:
            return "very-good";
        case 3:
            return "good";
        case 2:
            return "bad";
        case 1:
            return "very-bad";
        default:
            return "very-bad";
    }
};

const getBackgroundColor = (status: Status) => {
    switch (status) {
        case Status.GREEN:
            return "var(--green-300)";
        case Status.YELLOW:
            return "var(--yellow-200)";
        case Status.RED:
            return "var(--red-50)";
    }
};
